import { useEffect, useState, useRef } from "react";
import { api } from "./api";

// Polling cadence for the sidebar health dot. 30s is low-noise: not
// invasive enough to stress the backend on long dashboard sessions,
// fast enough to catch an incident within the usual "the app feels
// slow" reaction window.
const POLL_MS = 30000;

/**
 * Subscribes to `/admin/health-summary` and returns `{severity, reasons, …}`.
 *
 * Severity ladder is decided server-side (one source of truth for the
 * sidebar and any future surfaces — Telegram alerts, dashboard banner).
 *
 * Pauses when the browser tab is hidden so a forgotten tab doesn't
 * generate traffic all day.
 */
export function useHealthSummary() {
  const [data, setData] = useState({
    severity: "ok",
    reasons: [],
    pod_mem_percent: null,
    stuck_jobs: 0,
    recent_errors: 0,
    inflight_count: 0,
  });
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data: body } = await api.get("/admin/health-summary");
        if (alive) setData(body);
      } catch {
        // Silent: the sidebar dot stays at its last known value. A
        // transient 401 (session expired) or 5xx (backend restart) is
        // not worth a toast — the user will see errors on the main
        // route they're actually on.
      }
    };
    const start = () => {
      load();
      timerRef.current = setInterval(load, POLL_MS);
    };
    const stop = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    const onVis = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return data;
}
